/**
 * Open-choice assignments for estimated-time ranges.
 * Answered decisions stay fixed. Unanswered ones are enumerated
 * (or greedily sampled once the combination count gets large).
 */

import { buildChildrenMap, getEffectiveDependencies } from './dependencyGraph';

/** Cap exact choice enumeration. Above this, min/max uses one greedy scenario each. */
const MAX_CHOICE_COMBINATIONS = 8192;

export function missionTasksOf(tasks) {
  return (tasks || []).filter((task) => task.nodeType !== 'milestone' && task.nodeType !== 'note');
}

function createExclusionContext(tasks) {
  const childrenMap = buildChildrenMap(tasks);
  const depsByTask = new Map();
  const indegree = new Map();
  for (const task of tasks) {
    depsByTask.set(task.id, getEffectiveDependencies(task, tasks));
    indegree.set(task.id, 0);
  }
  for (const task of tasks) {
    for (const { childId } of childrenMap.get(task.id) || []) {
      if (!indegree.has(childId)) continue;
      indegree.set(childId, indegree.get(childId) + 1);
    }
  }

  const queue = [];
  for (const [id, degree] of indegree) {
    if (degree === 0) queue.push(id);
  }
  const topo = [];
  let cursor = 0;
  while (cursor < queue.length) {
    const id = queue[cursor];
    cursor += 1;
    topo.push(id);
    for (const { childId } of childrenMap.get(id) || []) {
      if (!indegree.has(childId)) continue;
      const next = indegree.get(childId) - 1;
      indegree.set(childId, next);
      if (next === 0) queue.push(childId);
    }
  }
  const cyclic = topo.length < tasks.length;
  if (cyclic) {
    const placed = new Set(topo);
    for (const task of tasks) {
      if (!placed.has(task.id)) topo.push(task.id);
    }
  }

  return { tasks, childrenMap, depsByTask, topo, cyclic };
}

function openDecisionChoices(tasks, childrenMap) {
  const open = [];
  for (const task of tasks) {
    if (task.nodeType !== 'decision' || task.answer) continue;
    const seen = new Set();
    const fromOptions = [];
    for (const option of task.options || []) {
      if (!option?.id || seen.has(option.id)) continue;
      seen.add(option.id);
      fromOptions.push(option.id);
    }
    if (fromOptions.length > 0) {
      open.push({ id: task.id, options: fromOptions });
      continue;
    }
    const branches = [];
    for (const { branch } of childrenMap.get(task.id) || []) {
      if (!branch || seen.has(branch)) continue;
      seen.add(branch);
      branches.push(branch);
    }
    if (branches.length > 0) open.push({ id: task.id, options: branches });
  }
  return open;
}

function enumerateAssignments(openDecisions) {
  let product = 1;
  for (const decision of openDecisions) {
    product *= decision.options.length;
    if (product > MAX_CHOICE_COMBINATIONS) return null;
  }
  const assignments = [];
  const current = new Map();
  const walk = (index) => {
    if (index >= openDecisions.length) {
      assignments.push(new Map(current));
      return;
    }
    const decision = openDecisions[index];
    for (const optionId of decision.options) {
      current.set(decision.id, optionId);
      walk(index + 1);
    }
    current.delete(decision.id);
  };
  walk(0);
  if (assignments.length === 0) assignments.push(new Map());
  return assignments;
}

function excludeForAssignment(ctx, answerById) {
  const excludedEdges = new Set();
  for (const task of ctx.tasks) {
    if (task.nodeType !== 'decision') continue;
    const selected = answerById.has(task.id) ? answerById.get(task.id) : task.answer;
    if (!selected) continue;
    for (const { childId, branch } of ctx.childrenMap.get(task.id) || []) {
      if (branch && branch !== selected) {
        excludedEdges.add(`${task.id}->${childId}`);
      }
    }
  }

  const excluded = new Set();
  const passes = ctx.cyclic ? ctx.tasks.length : 1;
  for (let pass = 0; pass < passes; pass += 1) {
    let changed = false;
    for (const id of ctx.topo) {
      if (excluded.has(id)) continue;
      const deps = ctx.depsByTask.get(id) || [];
      if (deps.length === 0) continue;
      const allExcluded = deps.every((dep) => (
        excludedEdges.has(`${dep.taskId}->${id}`) || excluded.has(dep.taskId)
      ));
      if (allExcluded) {
        excluded.add(id);
        changed = true;
      }
    }
    if (!ctx.cyclic || !changed) break;
  }
  return excluded;
}

function scoreAssignment(assignment, parts, exclude, missionTasks, pick) {
  const excluded = exclude(assignment);
  let score = 0;
  for (const task of missionTasks) {
    if (excluded.has(task.id)) continue;
    const part = parts.get(task.id);
    if (!part || part.skip) continue;
    score += pick === 'min' ? part.min : part.max;
  }
  return score;
}

function greedyAssignments(openDecisions, parts, exclude, missionTasks) {
  const pickOne = (pick) => {
    const assignment = new Map();
    for (const decision of openDecisions) {
      let best = decision.options[0];
      let bestScore = pick === 'min' ? Infinity : -Infinity;
      for (const optionId of decision.options) {
        const trial = new Map(assignment);
        trial.set(decision.id, optionId);
        const score = scoreAssignment(trial, parts, exclude, missionTasks, pick);
        const better = pick === 'min' ? score < bestScore : score > bestScore;
        if (better) {
          bestScore = score;
          best = optionId;
        }
      }
      assignment.set(decision.id, best);
    }
    return assignment;
  };
  return [pickOne('min'), pickOne('max')];
}

function assignmentsFor(openDecisions, parts, exclude, missionTasks) {
  const exact = enumerateAssignments(openDecisions);
  if (exact) return exact;
  return greedyAssignments(openDecisions, parts, exclude, missionTasks);
}

/**
 * Exclusion graph plus the list of choice assignments to score.
 * `listAssignments` needs each task's work part so the greedy fallback can rank options.
 */
export function buildChoiceSearch(missionTasks) {
  const ctx = createExclusionContext(missionTasks);
  const exclude = (assignment) => excludeForAssignment(ctx, assignment);
  return {
    ctx,
    exclude,
    listAssignments(parts) {
      const openDecisions = openDecisionChoices(missionTasks, ctx.childrenMap);
      return assignmentsFor(openDecisions, parts, exclude, missionTasks);
    },
  };
}
